// Phase 5 part 2 — Show Data drawer.
// Click a provenance chip → this drawer slides in from the right with the
// full audit chain for the answer:
//   • The clicked-on Provenance record (source, report name, dates, unit)
//   • Replay metadata (query_hash, model_version, data_vintage)
//   • Every tool call that fed the answer, with input + raw output preview
//
// Architecture: a React context exposes `openDrawer(provenance, messageId)`
// so any provenance chip rendered anywhere in the conversation can trigger
// the drawer. The drawer itself is mounted once at the consultant root.

import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { ExternalLink, Database, Clock, Hash, Bot, AlertCircle, ChevronRight, History, GitBranch } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useConsultantToolCalls, type ConsultantToolCallRow } from "@/hooks/useConsultantToolCalls";
import type { Provenance } from "@/types/consultantProvenance";

interface DrawerState {
  open: boolean;
  provenance: Provenance | null;
  messageId: string | null;
}

interface ProvenanceDrawerContextValue {
  openDrawer: (provenance: Provenance, messageId: string | null) => void;
  closeDrawer: () => void;
}

const ProvenanceDrawerContext = createContext<ProvenanceDrawerContextValue | null>(null);

export function useProvenanceDrawer(): ProvenanceDrawerContextValue {
  const ctx = useContext(ProvenanceDrawerContext);
  if (!ctx) {
    // Soft-fail: chip clicks without a provider just no-op.
    return { openDrawer: () => {}, closeDrawer: () => {} };
  }
  return ctx;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatJson(value: unknown, maxLines = 30): string {
  try {
    const s = JSON.stringify(value, null, 2);
    const lines = s.split("\n");
    if (lines.length <= maxLines) return s;
    return lines.slice(0, maxLines).join("\n") + `\n… (+${lines.length - maxLines} more lines)`;
  } catch {
    return String(value);
  }
}

function ToolCallCard({ call }: { call: ConsultantToolCallRow }) {
  const hasError = !!call.error;
  return (
    <div className="rounded-md border border-border bg-card p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Bot className="h-3.5 w-3.5 text-primary flex-shrink-0" />
          <span className="font-mono text-xs font-semibold truncate">{call.tool_name}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-shrink-0">
          {call.latency_ms != null ? <span>{call.latency_ms}ms</span> : null}
          {hasError ? <AlertCircle className="h-3 w-3 text-destructive" /> : null}
        </div>
      </div>

      {call.tool_input ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
            <ChevronRight className="inline h-3 w-3 -mt-0.5" /> input
          </summary>
          <pre className="mt-1.5 overflow-x-auto rounded bg-muted/40 p-2 text-[11px] leading-relaxed">{formatJson(call.tool_input, 20)}</pre>
        </details>
      ) : null}

      {call.raw_output != null ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
            <ChevronRight className="inline h-3 w-3 -mt-0.5" /> raw output
          </summary>
          <pre className="mt-1.5 overflow-x-auto rounded bg-muted/40 p-2 text-[11px] leading-relaxed max-h-72">{formatJson(call.raw_output, 60)}</pre>
        </details>
      ) : null}

      {hasError ? (
        <div className="text-[11px] text-destructive bg-destructive/10 rounded px-2 py-1">
          {call.error}
        </div>
      ) : null}
    </div>
  );
}

function RevisionHistoryBlock({ provenance }: { provenance: Provenance }) {
  const {
    revision_count,
    original_value,
    original_published_date,
    latest_published_date,
    revision_status,
  } = provenance;

  // Hide entirely for non-revising sources or when we have no revision data.
  if (revision_status === "n/a") return null;
  if (revision_count == null && original_value == null && original_published_date == null) {
    return null;
  }

  const hasRevisions = (revision_count ?? 0) >= 1;

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
        <History className="h-3.5 w-3.5" />
        Revision history
      </h3>

      {!hasRevisions ? (
        <p className="text-xs text-muted-foreground">
          No revisions yet — this is the original-print value.
          {original_published_date
            ? ` First reported on ${formatDateTime(original_published_date)}.`
            : null}
        </p>
      ) : (
        <dl className="grid grid-cols-[140px,1fr] gap-x-3 gap-y-1 text-xs">
          <dt className="text-muted-foreground">Revisions</dt>
          <dd className="text-foreground font-mono">
            {revision_count} {revision_count === 1 ? "revision" : "revisions"} since first publish
          </dd>

          {original_value != null ? (
            <>
              <dt className="text-muted-foreground">Original value</dt>
              <dd className="text-foreground font-mono">
                {original_value.toLocaleString()} {provenance.unit}
              </dd>
            </>
          ) : null}

          {original_published_date ? (
            <>
              <dt className="text-muted-foreground">First published</dt>
              <dd className="text-foreground">{formatDateTime(original_published_date)}</dd>
            </>
          ) : null}

          {latest_published_date ? (
            <>
              <dt className="text-muted-foreground">Latest revision</dt>
              <dd className="text-foreground">{formatDateTime(latest_published_date)}</dd>
            </>
          ) : null}
        </dl>
      )}

      <p className="mt-2 text-[10px] text-muted-foreground italic">
        USDA revises some values across multiple publishes — placements, slaughter, and inventory in particular. This panel surfaces the revision lineage so the consultant's quoted value can be traced back to its original print.
      </p>
    </div>
  );
}

/**
 * The upstream USDA reports a DERIVED metric was computed from.
 *
 * Calculated metrics (packer margin, market-pulse verdict, and ~9 other tools
 * that call buildDerivedProvenance) cite an `internal-derived` wrapper whose
 * `derived_from` array holds the real published reports. Until 2026-07-29 this
 * drawer rendered only the wrapper, so a user tapping the chip on a calculated
 * number saw "internal-derived" and NO USDA report at all — the "every number
 * traces to a published USDA report" claim was true in the database and not
 * demonstrable in the product. This block closes that gap.
 */
export function DerivedFromBlock({ provenance }: { provenance: Provenance }) {
  const upstream = Array.isArray(provenance.derived_from) ? provenance.derived_from : [];
  if (upstream.length === 0) return null;

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
        <GitBranch className="h-3.5 w-3.5" />
        Built from {upstream.length} published {upstream.length === 1 ? "report" : "reports"}
      </h3>
      <ol className="space-y-2">
        {upstream.map((u, i) => (
          <li
            key={`${u.report_slug}-${u.as_of_date}-${i}`}
            className="rounded border border-border/60 bg-muted/20 p-2"
          >
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border border-border text-[9px] font-semibold text-muted-foreground">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-foreground break-words">
                  {u.report_name}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                  <span>{u.source}</span>
                  <span>Slug {u.report_slug}</span>
                  {u.as_of_date ? <span>As of {u.as_of_date}</span> : null}
                  {u.unit ? <span>{u.unit}</span> : null}
                  {u.revision_status && u.revision_status !== "n/a" ? (
                    <span>Revision: {u.revision_status}</span>
                  ) : null}
                </div>
                {u.usda_url ? (
                  <a
                    href={u.usda_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-[10px] text-primary hover:underline break-all"
                  >
                    View on USDA
                    <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
                  </a>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-2 text-[10px] text-muted-foreground italic">
        This figure is calculated by RaisedBeef from the published reports above —
        it is not itself a USDA publication. Each row links to the source so the
        number can be re-derived independently.
      </p>
    </div>
  );
}

function ProvenanceDetailBlock({ provenance }: { provenance: Provenance }) {
  const fields: Array<[string, string]> = [
    ["Source", provenance.source],
    ["Report", provenance.report_name],
    ["Slug", provenance.report_slug],
    ["As of", provenance.as_of_date],
    ["Period (days)", provenance.period_days != null ? String(provenance.period_days) : "—"],
    ["Unit", provenance.unit],
    ["Revision", provenance.revision_status],
    ["Fetched at", formatDateTime(provenance.fetched_at)],
  ];
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
        <Database className="h-3.5 w-3.5" />
        This citation
      </h3>
      <dl className="grid grid-cols-[100px,1fr] gap-x-3 gap-y-1 text-xs">
        {fields.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="text-foreground break-words">{v}</dd>
          </div>
        ))}
        {provenance.usda_url ? (
          <>
            <dt className="text-muted-foreground">URL</dt>
            <dd>
              <a
                href={provenance.usda_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline break-all"
              >
                {provenance.usda_url}
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </a>
            </dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

function ReplayMetadataBlock({
  message,
  isLoading,
}: {
  message: { query_hash: string | null; model_version: string | null; data_vintage: string | null; token_count: number | null; status: string | null } | null;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-card p-3">
        <div className="text-xs text-muted-foreground">Loading replay metadata…</div>
      </div>
    );
  }
  if (!message) {
    return (
      <div className="rounded-md border border-border bg-card p-3">
        <div className="text-xs text-muted-foreground">No replay metadata recorded for this message.</div>
      </div>
    );
  }
  const fields: Array<[string, string]> = [
    ["Query hash", message.query_hash ? `${message.query_hash.slice(0, 16)}…` : "—"],
    ["Model", message.model_version ?? "—"],
    ["Data vintage", formatDateTime(message.data_vintage)],
    ["Tokens", message.token_count != null ? String(message.token_count) : "—"],
    ["Status", message.status ?? "—"],
  ];
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
        <Hash className="h-3.5 w-3.5" />
        Replay metadata
      </h3>
      <dl className="grid grid-cols-[100px,1fr] gap-x-3 gap-y-1 text-xs">
        {fields.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="text-foreground break-words font-mono text-[11px]">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2 text-[10px] text-muted-foreground italic">
        Replay-ready: a future Phase-1 RPC can re-run this same answer turn against this exact data vintage and produce a byte-equivalent result for audit.
      </p>
    </div>
  );
}

function DrawerBody({ state }: { state: DrawerState }) {
  const { provenance, messageId } = state;
  const { data, isLoading, error } = useConsultantToolCalls(messageId, {
    enabled: state.open,
  });

  if (!provenance) return null;

  return (
    <div className="space-y-3 mt-4">
      <ProvenanceDetailBlock provenance={provenance} />
      {/* Directly under the citation: for a derived metric this is where the
          actual USDA reports live. Renders nothing for a direct citation. */}
      <DerivedFromBlock provenance={provenance} />
      <RevisionHistoryBlock provenance={provenance} />
      <ReplayMetadataBlock message={data?.message ?? null} isLoading={isLoading} />

      <div>
        <h3 className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
          <Clock className="h-3.5 w-3.5" />
          Tool call chain {data?.toolCalls.length ? `(${data.toolCalls.length})` : ""}
        </h3>
        {error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            Could not load tool calls: {(error as Error).message}
          </div>
        ) : isLoading ? (
          <div className="rounded-md border border-border bg-card p-3 text-xs text-muted-foreground">Loading tool calls…</div>
        ) : !data?.toolCalls.length ? (
          <div className="rounded-md border border-border bg-card p-3 text-xs text-muted-foreground">
            No tool calls recorded. The answer may have been a refusal or pure prose.
          </div>
        ) : (
          <div className="space-y-2">
            {data.toolCalls.map((call) => (
              <ToolCallCard key={call.id} call={call} />
            ))}
          </div>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground italic px-1">
        This panel is the audit trail for the answer above it. Every numeric value the consultant produced traces back through this chain to a USDA report or DV Auction transaction. No fabrication, no model memory — full retrieve-don't-recall contract per CONSULTANT_STANDARDS.md §1.
      </p>
    </div>
  );
}

export function ProvenanceDrawerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DrawerState>({ open: false, provenance: null, messageId: null });

  const value = useMemo<ProvenanceDrawerContextValue>(
    () => ({
      openDrawer: (provenance, messageId) =>
        setState({ open: true, provenance, messageId }),
      closeDrawer: () => setState((s) => ({ ...s, open: false })),
    }),
    [],
  );

  return (
    <ProvenanceDrawerContext.Provider value={value}>
      {children}
      <Sheet
        open={state.open}
        onOpenChange={(next) => {
          if (!next) setState((s) => ({ ...s, open: false }));
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-base">Show Data</SheetTitle>
            <SheetDescription className="text-xs">
              Full audit chain for this answer — source, replay metadata, and the tool calls that produced the numbers.
            </SheetDescription>
          </SheetHeader>
          <DrawerBody state={state} />
        </SheetContent>
      </Sheet>
    </ProvenanceDrawerContext.Provider>
  );
}
